(function () {
  'use strict';

  if (customElements.get('sak-bundle-builder')) return;

  class SakBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.core = window.SakBundleCore;
      this.product = this.parseProductData();
      this.cardsContainer = this.querySelector('[data-bundle-cards]');
      this.summaryCount = this.querySelector('[data-bundle-summary-count]');
      this.summaryItems = this.querySelector('[data-bundle-summary-items]');
      this.subtotal = this.querySelector('[data-bundle-subtotal]');
      this.tier = this.querySelector('[data-bundle-tier]');
      this.error = this.querySelector('[data-bundle-error]');
      this.submitButton = this.querySelector('[data-bundle-submit]');
      this.submitText = this.querySelector('[data-bundle-submit-text]');
      this.spinner = this.querySelector('[data-bundle-spinner]');
      this.pending = false;

      if (!this.core || !this.product || !this.cardsContainer || !this.submitButton) {
        this.showError('The bundle builder could not load. Please refresh the page or contact us for help.');
        return;
      }

      const selectedSize = this.querySelector('[data-bundle-size]:checked');
      const initialSize = selectedSize ? Number(selectedSize.value) : this.core.MIN_SIZE;
      this.cards = this.createCards(initialSize);

      this.querySelectorAll('[data-bundle-size]').forEach((input) => {
        input.addEventListener('change', (event) => {
          if (event.target.checked) this.setBundleSize(Number(event.target.value));
        });
      });

      this.submitButton.addEventListener('click', () => this.submitBundle());
      this.renderCards();
      this.updateSummary();
    }

    parseProductData() {
      const dataElement = this.querySelector('[data-sak-bundle-product]');
      if (!dataElement) return null;

      try {
        const product = JSON.parse(dataElement.textContent || '{}');
        if (!Array.isArray(product.options) || !Array.isArray(product.variants)) return null;
        return product;
      } catch (_error) {
        return null;
      }
    }

    defaultOptions() {
      const availableVariant = this.product.variants.find((variant) => variant.available);
      if (availableVariant && Array.isArray(availableVariant.options)) {
        return availableVariant.options.slice();
      }

      return this.product.options.map((option) => (option.values && option.values[0]) || '');
    }

    createCards(size) {
      return Array.from({ length: size }, () => ({ options: this.defaultOptions(), name: '' }));
    }

    setBundleSize(size) {
      if (this.pending || size < this.core.MIN_SIZE || size > this.core.MAX_SIZE) return;

      if (size > this.cards.length) {
        while (this.cards.length < size) {
          this.cards.push({ options: this.defaultOptions(), name: '' });
        }
      } else {
        this.cards = this.cards.slice(0, size);
      }

      this.hideError();
      this.renderCards();
      this.updateSummary();
    }

    renderCards() {
      const fragment = document.createDocumentFragment();

      this.cards.forEach((card, cardIndex) => {
        fragment.append(this.createCardElement(card, cardIndex));
      });

      this.cardsContainer.replaceChildren(fragment);
    }

    createCardElement(card, cardIndex) {
      const slot = cardIndex + 1;
      const cardElement = document.createElement('article');
      cardElement.className = 'sak-bundle-card';
      cardElement.dataset.slot = String(slot);

      const heading = document.createElement('h2');
      heading.className = 'sak-bundle-card__heading';

      const number = document.createElement('span');
      number.className = 'sak-bundle-card__number';
      number.setAttribute('aria-hidden', 'true');
      number.textContent = String(slot);

      const headingText = document.createElement('span');
      headingText.textContent = 'Bag ' + slot;
      heading.append(number, headingText);

      const fields = document.createElement('div');
      fields.className = 'sak-bundle-card__fields';

      this.product.options.forEach((option, optionIndex) => {
        fields.append(this.createOptionField(card, cardIndex, option, optionIndex, cardElement));
      });

      const nameField = this.createNameField(card, cardIndex, cardElement);
      fields.append(nameField);

      const status = document.createElement('p');
      status.className = 'sak-bundle-card__status caption';
      status.dataset.cardStatus = '';

      const error = document.createElement('p');
      error.className = 'sak-bundle-card__error';
      error.dataset.cardError = '';
      error.setAttribute('role', 'alert');
      error.hidden = true;

      cardElement.append(heading, fields, status, error);
      this.updateCardElement(cardElement, card);
      return cardElement;
    }

    createOptionField(card, cardIndex, option, optionIndex, cardElement) {
      const field = document.createElement('div');
      field.className = 'sak-bundle-card__field';

      const id = 'SakBundle-' + this.dataset.sectionId + '-' + (cardIndex + 1) + '-Option-' + optionIndex;
      const label = document.createElement('label');
      label.className = 'sak-bundle-card__label';
      label.setAttribute('for', id);
      label.textContent = option.name;

      const select = document.createElement('select');
      select.className = 'sak-bundle-card__select';
      select.id = id;
      select.dataset.optionIndex = String(optionIndex);

      (option.values || []).forEach((value) => {
        const optionElement = document.createElement('option');
        optionElement.value = value;
        optionElement.textContent = value;
        optionElement.selected = card.options[optionIndex] === value;
        select.append(optionElement);
      });

      select.addEventListener('change', (event) => {
        card.options[optionIndex] = event.target.value;
        if (!this.core.isPersonalised(card.options)) card.name = '';
        this.updateCardElement(cardElement, card);
        this.hideError();
        this.updateSummary();
      });

      field.append(label, select);
      return field;
    }

    createNameField(card, cardIndex, cardElement) {
      const field = document.createElement('div');
      field.className = 'sak-bundle-card__field';
      field.dataset.nameField = '';

      const id = 'SakBundle-' + this.dataset.sectionId + '-' + (cardIndex + 1) + '-Name';
      const label = document.createElement('label');
      label.className = 'sak-bundle-card__label';
      label.setAttribute('for', id);
      label.textContent = 'Name for bag ' + (cardIndex + 1);

      const control = document.createElement('div');
      control.className = 'sak-bundle-card__name-control';

      const input = document.createElement('input');
      input.className = 'sak-bundle-card__name-input';
      input.type = 'text';
      input.id = id;
      input.maxLength = this.core.MAX_NAME_LENGTH;
      input.autocomplete = 'off';
      input.placeholder = 'e.g. ghia';
      input.dataset.nameInput = '';
      input.value = card.name;

      const suffix = document.createElement('span');
      suffix.className = 'sak-bundle-card__suffix';
      suffix.setAttribute('aria-hidden', 'true');
      suffix.textContent = '.sak';

      const hint = document.createElement('p');
      hint.className = 'sak-bundle-card__hint caption';
      hint.textContent = 'Max 12 characters before .sak';

      input.addEventListener('input', (event) => {
        const normalized = this.core.normalizeName(event.target.value);
        card.name = normalized;
        if (event.target.value !== normalized) event.target.value = normalized;
        this.updateCardElement(cardElement, card);
        this.hideError();
        this.updateSummary();
      });

      control.append(input, suffix);
      field.append(label, control, hint);
      return field;
    }

    updateCardElement(cardElement, card) {
      const personalised = this.core.isPersonalised(card.options);
      const nameField = cardElement.querySelector('[data-name-field]');
      const nameInput = cardElement.querySelector('[data-name-input]');
      const status = cardElement.querySelector('[data-card-status]');
      const variant = this.core.findVariant(this.product.variants, card.options);

      if (nameField && nameInput) {
        nameField.hidden = !personalised;
        nameInput.required = personalised;
        if (!personalised) nameInput.value = '';
      }

      if (!status) return;

      if (!variant) {
        status.textContent = 'Choose all options to continue.';
      } else if (!variant.available) {
        status.textContent = 'This combination is currently unavailable.';
      } else {
        status.textContent = this.formatMoney(variant.price);
      }
    }

    formatMoney(cents) {
      const currency = this.product.currency || 'ZAR';

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-ZA', {
          style: 'currency',
          currency,
        }).format(Number(cents || 0) / 100);
      } catch (_error) {
        return currency + ' ' + (Number(cents || 0) / 100).toFixed(2);
      }
    }

    updateSummary() {
      const subtotal = this.cards.reduce((sum, card) => {
        const variant = this.core.findVariant(this.product.variants, card.options);
        return sum + (variant ? Number(variant.price || 0) : 0);
      }, 0);
      const discount = this.core.discountForCount(this.cards.length);
      const validation = this.core.validateCards(this.cards, this.product.variants);

      this.summaryCount.textContent = this.cards.length + ' bags configured';
      this.subtotal.textContent = this.formatMoney(subtotal);
      this.tier.textContent = discount + '% bundle saving expected';

      const fragment = document.createDocumentFragment();
      this.cards.forEach((card, index) => {
        const variant = this.core.findVariant(this.product.variants, card.options);
        const item = document.createElement('li');
        item.className = 'sak-bundle-builder__summary-item';

        let text = 'Bag ' + (index + 1) + ': ' + card.options.join(' / ');
        if (this.core.isPersonalised(card.options) && this.core.fullCustomText(card.name)) {
          text += ' · ' + this.core.fullCustomText(card.name);
        }
        if (variant) text += ' · ' + this.formatMoney(variant.price);

        item.textContent = text;
        fragment.append(item);
      });
      this.summaryItems.replaceChildren(fragment);

      if (!this.pending) this.submitButton.disabled = !validation.valid;
    }

    clearCardErrors() {
      this.querySelectorAll('.sak-bundle-card').forEach((cardElement) => {
        cardElement.removeAttribute('aria-invalid');
        const error = cardElement.querySelector('[data-card-error]');
        if (error) {
          error.textContent = '';
          error.hidden = true;
        }
      });
    }

    showValidationErrors(errors) {
      this.clearCardErrors();

      errors.forEach((validationError) => {
        if (!validationError.slot) return;
        const cardElement = this.querySelector('[data-slot="' + validationError.slot + '"]');
        if (!cardElement) return;

        cardElement.setAttribute('aria-invalid', 'true');
        const error = cardElement.querySelector('[data-card-error]');
        if (error && error.hidden) {
          error.textContent = validationError.message;
          error.hidden = false;
        }
      });

      const firstError = errors[0];
      const firstCard = firstError && firstError.slot
        ? this.querySelector('[data-slot="' + firstError.slot + '"]')
        : null;
      const focusTarget = firstCard
        ? firstError.field === 'name'
          ? firstCard.querySelector('[data-name-input]')
          : firstCard.querySelector('select')
        : this.querySelector('[data-bundle-size]');

      if (focusTarget) focusTarget.focus();
    }

    createBundleId() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }

      return 'sak-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }

    setPending(pending) {
      this.pending = pending;
      this.querySelectorAll('input, select').forEach((control) => {
        control.disabled = pending;
      });
      this.submitButton.disabled = pending;
      this.submitButton.setAttribute('aria-disabled', pending ? 'true' : 'false');
      this.submitText.textContent = pending ? 'Adding your bundle…' : 'Add bundle to bag';
      this.spinner.classList.toggle('hidden', !pending);

      if (!pending) this.updateSummary();
    }

    async submitBundle() {
      if (this.pending) return;

      const validation = this.core.validateCards(this.cards, this.product.variants);
      if (!validation.valid) {
        this.showValidationErrors(validation.errors);
        this.showError(validation.errors[0].message);
        return;
      }

      this.clearCardErrors();
      this.hideError();
      this.setPending(true);

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const sectionDescriptors = cart && typeof cart.getSectionsToRender === 'function'
        ? cart.getSectionsToRender()
        : [];
      const sections = sectionDescriptors.map((section) => section.id).filter(Boolean);
      const items = this.core.buildCartItems(this.cards, this.product.variants, this.createBundleId());
      const body = { items, sections, sections_url: window.location.pathname };
      let cartMutated = false;

      try {
        if (cart && typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(document.activeElement);
        }

        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Shopify could not add this bundle.');
        }

        cartMutated = true;

        if (cart) {
          const sectionsAreComplete =
            data.sections && sections.every((sectionId) => typeof data.sections[sectionId] === 'string');

          if (!sectionsAreComplete) {
            throw new Error('The bundle was added, but the cart could not refresh.');
          }

          cart.renderContents(data);
        } else {
          window.location.assign(window.Shopify.routes.root + 'cart');
          return;
        }

        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'sak-bundle-builder',
            productVariantId: items[0].id,
            cartData: data,
          });
        }

        this.cards = this.createCards(this.cards.length);
        this.renderCards();
        this.hideError();
      } catch (error) {
        if (cartMutated) {
          window.location.assign(window.Shopify.routes.root + 'cart');
          return;
        }

        this.showError(error && error.message ? error.message : 'We could not add your bundle. Please try again.');
      } finally {
        this.setPending(false);
      }
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    hideError() {
      if (!this.error) return;
      this.error.textContent = '';
      this.error.hidden = true;
    }
  }

  customElements.define('sak-bundle-builder', SakBundleBuilder);
})();
